import React from "react";
import { Card, Container, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Container className="py-4">
      <Card>
        <Card.Body className="p-4">
          <h1 className="mb-4">Privacy Policy for QuVikme</h1>

          <Alert variant="info" className="mb-4">
            <strong>Effective Date:</strong> December 21, 2025
            <br />
            <strong>Last Updated:</strong> December 21, 2025
          </Alert>

          <p>
            Welcome to QuVikme! We are committed to protecting your privacy and
            ensuring the security of your personal information. This Privacy
            Policy explains how QuVikme ("we," "us," or "our") collects, uses,
            shares, and protects information when you use our mobile application
            (the "App") and related services.
          </p>

          <Alert variant="warning" className="mb-4">
            <strong>Important:</strong> By downloading, installing, or using the
            QuVikme app, you agree to the collection and use of information in
            accordance with this Privacy Policy. If you do not agree with our
            policies and practices, please do not use our App.
          </Alert>

          <section className="mb-4">
            <h2 className="h4 mb-3">1. Information We Collect</h2>

            <h3 className="h5 mb-2">1.1 Information You Provide Directly</h3>
            <p>
              We collect information that you voluntarily provide when using our
              App:
            </p>

            <h4 className="h6 mb-2 mt-3">For All Users:</h4>
            <ul>
              <li>
                <strong>Account Information:</strong> Name, email address, phone
                number, password
              </li>
              <li>
                <strong>Profile Information:</strong> Profile picture, preferred
                language (English, Hindi, Marathi)
              </li>
              <li>
                <strong>Contact Information:</strong> Billing address, service
                delivery addresses
              </li>
              <li>
                <strong>Payment Information:</strong> Credit card details, UPI
                information, bank account details (processed securely through
                our payment processor)
              </li>
              <li>
                <strong>Communication Data:</strong> Messages sent through our
                in-app messaging system, customer support communications
              </li>
              <li>
                <strong>Referral Information:</strong> Referral codes shared and
                used, contacts you choose to invite
              </li>
            </ul>

            <h4 className="h6 mb-2 mt-3">
              Additional Information for Vendors (Service Providers):
            </h4>
            <ul>
              <li>
                <strong>Business Information:</strong> Business name, business
                category, sub-category, business description
              </li>
              <li>
                <strong>Professional Details:</strong> Services offered, pricing
                information, service descriptions, years of experience,
                education, skills, certifications
              </li>
              <li>
                <strong>Business Documentation:</strong> Business certificates,
                licenses, insurance documents, professional certifications
              </li>
              <li>
                <strong>KYC (Know Your Customer) Information:</strong>{" "}
                Government-issued ID, address proof, business registration
                documents
              </li>
              <li>
                <strong>Financial Information:</strong> Bank account details,
                UPI ID, GST number, tax information
              </li>
              <li>
                <strong>Portfolio:</strong> Images and videos of previous work,
                before/after photos, customer testimonials
              </li>
              <li>
                <strong>Availability Information:</strong> Working hours,
                working days, service areas, emergency service availability
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">
              1.2 Information Collected Automatically
            </h3>
            <p>
              When you use the QuVikme App, we automatically collect certain
              information:
            </p>

            <ul>
              <li>
                <strong>Location Information:</strong>
                <ul>
                  <li>
                    Real-time GPS location when you use location-based features
                  </li>
                  <li>
                    Vendor location for matching users with nearby service
                    providers (within 10km radius)
                  </li>
                  <li>Service delivery addresses</li>
                  <li>Geographic coordinates (latitude and longitude)</li>
                </ul>
              </li>
              <li>
                <strong>Device Information:</strong>
                <ul>
                  <li>Device type, model, and manufacturer</li>
                  <li>Operating system and version</li>
                  <li>Unique device identifiers</li>
                  <li>Mobile network information</li>
                  <li>IP address</li>
                </ul>
              </li>
              <li>
                <strong>Usage Information:</strong>
                <ul>
                  <li>App features accessed and used</li>
                  <li>Time and date of app usage</li>
                  <li>Service requests created and bookings made</li>
                  <li>Search queries and filters used</li>
                  <li>Interaction with advertisements</li>
                </ul>
              </li>
              <li>
                <strong>Camera and Photos:</strong> When you upload images
                (profile pictures, business photos, KYC documents), we access
                your device camera and photo library with your permission
              </li>
              <li>
                <strong>Notifications:</strong> Push notification tokens and
                notification interaction data
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">
              1.3 Information from Third-Party Sources
            </h3>
            <ul>
              <li>
                <strong>Google Sign-In:</strong> If you choose to sign in with
                Google, we receive basic profile information from your Google
                account
              </li>
              <li>
                <strong>Payment Processors:</strong> Transaction information
                from Razorpay for payment processing and verification
              </li>
              <li>
                <strong>Cloud Infrastructure Services:</strong> Database analytics and crash
                monitoring data from cloud infrastructure providers
              </li>
              <li>
                <strong>Geocoding Services:</strong> Address verification and
                coordinate data from mapping services
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">2. How We Use Your Information</h2>

            <p>We use the collected information for the following purposes:</p>

            <h3 className="h5 mb-2">2.1 To Provide and Improve Our Services</h3>
            <ul>
              <li>Create and manage your user or vendor account</li>
              <li>Process service requests and bookings</li>
              <li>
                Match users with nearby service providers (within 10km radius)
              </li>
              <li>Facilitate communication between users and vendors</li>
              <li>Process payments, subscriptions, and withdrawals</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Verify vendor credentials and KYC information</li>
              <li>Display vendor advertisements to users</li>
              <li>Calculate and track referral rewards (10 points = 10 INR)</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">
              2.2 For Security and Fraud Prevention
            </h3>
            <ul>
              <li>Verify user and vendor identities</li>
              <li>Prevent fraudulent transactions and abuse</li>
              <li>Monitor and analyze security threats</li>
              <li>Enforce our Terms and Conditions</li>
              <li>
                Comply with legal obligations and law enforcement requests
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">2.3 For Analytics and Improvements</h3>
            <ul>
              <li>Analyze app usage patterns and trends</li>
              <li>Improve app features and user experience</li>
              <li>Develop new services and features</li>
              <li>Track vendor performance and earnings</li>
              <li>Generate business analytics and reports</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">2.4 For Communication</h3>
            <ul>
              <li>
                Send service-related notifications (booking confirmations,
                status updates)
              </li>
              <li>
                Send subscription expiration reminders (15 days before
                expiration)
              </li>
              <li>Provide customer support</li>
              <li>
                Send promotional offers and marketing communications (with your
                consent)
              </li>
              <li>Notify you about app updates and new features</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">3. How We Share Your Information</h2>

            <p>We share your information in the following circumstances:</p>

            <h3 className="h5 mb-2">3.1 With Other Users of the Platform</h3>
            <ul>
              <li>
                <strong>Vendor Information Visible to Users:</strong> When users
                browse services, they can see vendor business profiles, services
                offered, pricing, reviews, ratings, location (within 10km
                radius), and contact information
              </li>
              <li>
                <strong>User Information Visible to Vendors:</strong> When a
                user creates a service request, matched vendors (within 10km)
                receive the user's name, service requirements, preferred
                date/time, budget range, and service address
              </li>
              <li>
                <strong>Booking Information:</strong> Both parties in a booking
                can see each other's contact information and communicate through
                our messaging system
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">
              3.2 With Service Providers and Business Partners
            </h3>
            <ul>
              <li>
                <strong>MongoDB Cloud Infrastructure:</strong> For database storage, data management,
                secure API operations, and session management
              </li>
              <li>
                <strong>Razorpay:</strong> For payment processing, subscription
                management, and payout processing
              </li>
              <li>
                <strong>Geocoding Services:</strong> For address verification
                and location coordinate calculation
              </li>
              <li>
                <strong>Cloud Storage Providers:</strong> For storing uploaded
                documents, images, and files
              </li>
              <li>
                <strong>SMS and Email Services:</strong> For sending
                notifications and communications
              </li>
              <li>
                <strong>Analytics Providers:</strong> For app performance
                monitoring and analytics
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">3.3 With Administrators</h3>
            <ul>
              <li>
                <strong>Super Admins:</strong> Have access to all vendor and
                user data for platform management
              </li>
              <li>
                <strong>Sub-Admins:</strong> Have access to vendor and user data
                within their assigned pincode area
              </li>
              <li>
                <strong>Sub-Sub Admins:</strong> Have access to vendor and user
                data within their assigned society
              </li>
              <li>
                Admin access includes profile information, bookings,
                transactions, KYC documents, and analytics
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">3.4 For Legal and Safety Reasons</h3>
            <ul>
              <li>
                When required by law, legal process, or government request
              </li>
              <li>To enforce our Terms and Conditions and policies</li>
              <li>
                To protect the rights, property, or safety of QuVikme, our
                users, or the public
              </li>
              <li>
                In connection with fraud prevention and security investigations
              </li>
              <li>
                To comply with tax, accounting, or regulatory requirements
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">3.5 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, reorganization, or sale of
              assets, your information may be transferred to the successor
              entity.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">4. Location Information</h2>

            <p>
              QuVikme uses location information as a core feature of the
              platform:
            </p>

            <h3 className="h5 mb-2">4.1 How We Use Location</h3>
            <ul>
              <li>
                <strong>Vendor Matching:</strong> We match users with service
                providers within a 10-kilometer radius based on GPS coordinates
              </li>
              <li>
                <strong>Service Requests:</strong> When users create service
                requests, we identify and notify vendors in the nearby area
                (within 10km)
              </li>
              <li>
                <strong>Address Geocoding:</strong> We convert addresses to
                geographic coordinates for accurate location-based services
              </li>
              <li>
                <strong>Live Location Tracking:</strong> Vendors can update
                their current location for accurate proximity matching
              </li>
              <li>
                <strong>Service Area Display:</strong> We display vendors'
                service coverage areas to users
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">4.2 Location Permissions</h3>
            <ul>
              <li>
                You can enable or disable location services in your device
                settings
              </li>
              <li>
                Location permission is required for vendors to receive service
                requests
              </li>
              <li>
                Users need location permission to find nearby vendors and set
                service addresses
              </li>
              <li>Disabling location services may limit app functionality</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">4.3 Location Accuracy</h3>
            <p>
              We use GPS, Wi-Fi, and mobile network data to determine your
              location. Location accuracy depends on device capabilities and
              environmental factors. The 10km radius matching is calculated
              based on straight-line distance between coordinates.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">5. Payment and Financial Information</h2>

            <h3 className="h5 mb-2">5.1 Payment Processing</h3>
            <ul>
              <li>
                We use Razorpay, a third-party payment processor, to handle all
                payment transactions
              </li>
              <li>
                Your payment information (credit card, UPI) is encrypted and
                transmitted directly to Razorpay
              </li>
              <li>
                We do not store complete credit card numbers on our servers
              </li>
              <li>
                We retain transaction records for accounting and dispute
                resolution
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">5.2 Vendor Financial Information</h3>
            <ul>
              <li>
                Vendor bank account and UPI details are stored securely for
                withdrawal processing
              </li>
              <li>
                KYC verification is mandatory before vendors can request
                withdrawals
              </li>
              <li>Minimum withdrawal amount is 500 INR</li>
              <li>
                We track vendor earnings, platform fees, and withdrawal history
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">5.3 Taxes and Fees</h3>
            <ul>
              <li>
                <strong>User Taxes:</strong> GST and service tax are collected
                during payment
              </li>
              <li>
                <strong>Vendor Platform Fees:</strong> Platform fees are
                deducted from vendor earnings
              </li>
              <li>
                All tax calculations are performed in compliance with Indian tax
                regulations
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">6. Data Security</h2>

            <p>
              We implement industry-standard security measures to protect your
              information:
            </p>

            <ul>
              <li>
                <strong>Encryption:</strong> Data transmission is encrypted
                using SSL/TLS protocols
              </li>
              <li>
                <strong>Database Security:</strong> Data is securely stored in MongoDB
                Cloud databases with encrypted connections, security rules, and access controls
              </li>
              <li>
                <strong>Secure Authentication:</strong> JWT token-based Authentication
                with password hashing and secure token management
              </li>
              <li>
                <strong>Payment Security:</strong> PCI DSS compliant payment
                processing through Razorpay
              </li>
              <li>
                <strong>Secure Storage:</strong> Sensitive data is encrypted at
                rest using flutter_secure_storage
              </li>
              <li>
                <strong>Access Controls:</strong> Role-based access for admins
                with hierarchical permissions
              </li>
              <li>
                <strong>Regular Monitoring:</strong> Continuous monitoring for
                security threats and vulnerabilities
              </li>
              <li>
                <strong>KYC Verification:</strong> Document verification process
                to ensure vendor authenticity
              </li>
            </ul>

            <Alert variant="warning" className="mt-3">
              <strong>Please Note:</strong> While we strive to protect your
              information, no method of transmission over the internet or
              electronic storage is 100% secure. We cannot guarantee absolute
              security, but we continuously work to enhance our security
              practices.
            </Alert>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">7. Your Rights and Choices</h2>

            <h3 className="h5 mb-2">7.1 Access and Update</h3>
            <ul>
              <li>
                You can access and update your profile information at any time
                through the app settings
              </li>
              <li>
                You can view your booking history, transaction records, and
                subscription details
              </li>
              <li>
                Vendors can manage their service offerings, pricing, and
                availability
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">7.2 Delete Your Account</h3>
            <ul>
              <li>You have the right to delete your account at any time</li>
              <li>
                To delete your account, go to Settings &gt; Account &gt; Delete
                Account
              </li>
              <li>
                Upon account deletion, your personal information will be removed
                from our active databases
              </li>
              <li>
                Some information may be retained for legal, tax, or dispute
                resolution purposes
              </li>
              <li>
                Deletion may take up to 30 days to complete across all systems
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">7.3 Data Portability</h3>
            <ul>
              <li>
                You can request a copy of your personal data in a structured,
                machine-readable format
              </li>
              <li>
                Contact us at the email provided below to request your data
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">7.4 Marketing Communications</h3>
            <ul>
              <li>
                You can opt out of promotional emails by clicking "unsubscribe"
                in any marketing email
              </li>
              <li>
                You can manage push notification preferences in your device
                settings or app settings
              </li>
              <li>
                You cannot opt out of service-related communications (booking
                confirmations, subscription reminders)
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">7.5 Location Services</h3>
            <ul>
              <li>You can disable location services in your device settings</li>
              <li>
                Note that disabling location will limit core app functionality
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">7.6 Cookie Preferences</h3>
            <ul>
              <li>
                You can manage cookie preferences through your device browser
                settings
              </li>
              <li>
                The app may use cookies for analytics and performance tracking
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">8. Data Retention</h2>

            <p>
              We retain your information for as long as necessary to provide our
              services and comply with legal obligations:
            </p>

            <ul>
              <li>
                <strong>Active Accounts:</strong> Information is retained while
                your account is active
              </li>
              <li>
                <strong>Deleted Accounts:</strong> Most personal information is
                deleted within 30 days of account deletion
              </li>
              <li>
                <strong>Transaction Records:</strong> Retained for 7 years for
                tax and accounting purposes
              </li>
              <li>
                <strong>KYC Documents:</strong> Retained for 5 years after
                account closure for regulatory compliance
              </li>
              <li>
                <strong>Booking History:</strong> Retained for 2 years after
                booking completion for dispute resolution
              </li>
              <li>
                <strong>Communication Logs:</strong> Retained for 1 year for
                customer support purposes
              </li>
              <li>
                <strong>Analytics Data:</strong> Anonymized and aggregated data
                may be retained indefinitely
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">9. Third-Party Services</h2>

            <p>Our app integrates with the following third-party services:</p>

            <h3 className="h5 mb-2">9.1 MongoDB Cloud Infrastructure</h3>
            <ul>
              <li>
                Database management, session storage, security, and API infrastructure
              </li>
              <li>
                Privacy Policy:{" "}
                <a
                  href="https://www.mongodb.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                >
                  https://www.mongodb.com/legal/privacy-policy
                </a>
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">9.2 Razorpay</h3>
            <ul>
              <li>Payment processing and subscription management</li>
              <li>
                Privacy Policy:{" "}
                <a
                  href="https://razorpay.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                >
                  https://razorpay.com/privacy
                </a>
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">9.3 Google Maps</h3>
            <ul>
              <li>Location services and mapping</li>
              <li>
                Privacy Policy:{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                >
                  https://policies.google.com/privacy
                </a>
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">9.4 Google Sign-In</h3>
            <ul>
              <li>Optional authentication method</li>
              <li>
                Privacy Policy:{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                >
                  https://policies.google.com/privacy
                </a>
              </li>
            </ul>

            <p>
              These third-party services have their own privacy policies. We
              encourage you to review them.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">10. Children's Privacy</h2>

            <p>
              QuVikme is not intended for use by individuals under the age of
              18. We do not knowingly collect personal information from children
              under 18. If you are a parent or guardian and believe your child
              has provided us with personal information, please contact us
              immediately. We will take steps to delete such information from
              our systems.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">11. International Data Transfers</h2>

            <p>
              Your information may be transferred to and processed in countries
              other than India, including regions where our cloud database and
              infrastructure service providers operate servers. These countries may have
              different data protection laws. By using our app, you consent to
              the transfer of your information to these countries. We ensure
              appropriate safeguards are in place to protect your information in
              accordance with this Privacy Policy.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">12. Subscription and Grace Period</h2>

            <ul>
              <li>
                Users and vendors have a 45-day grace period for subscription
                renewal
              </li>
              <li>
                Daily notifications are sent 15 days before subscription
                expiration
              </li>
              <li>
                During grace period, limited functionality may be available
              </li>
              <li>Subscription data is used to determine access to features</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">13. Referral Program</h2>

            <ul>
              <li>
                Both users and vendors receive unique referral codes upon
                registration
              </li>
              <li>Successful referrals earn 10 points (equal to 10 INR)</li>
              <li>
                Referral codes can be shared via social media and messaging apps
              </li>
              <li>
                We track referral sign-ups and conversions for reward
                distribution
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">14. Advertisements</h2>

            <h3 className="h5 mb-2">14.1 For Vendors</h3>
            <ul>
              <li>
                Vendors can create and publish advertisements within the app
              </li>
              <li>
                All advertisements require admin approval before being displayed
                to users
              </li>
              <li>
                We collect information about ad creation, category, pricing, and
                performance
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">14.2 For Users</h3>
            <ul>
              <li>
                Users see approved vendor advertisements in their dashboard
              </li>
              <li>We track ad views and clicks for analytics purposes</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">15. Changes to This Privacy Policy</h2>

            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, technology, legal requirements, or other
              factors. When we make changes:
            </p>

            <ul>
              <li>
                We will update the "Last Updated" date at the top of this policy
              </li>
              <li>
                For material changes, we will notify you through the app or via
                email
              </li>
              <li>
                We will provide at least 30 days' notice before implementing
                significant changes
              </li>
              <li>
                Continued use of the app after changes constitutes acceptance of
                the updated policy
              </li>
            </ul>

            <p>
              We encourage you to review this Privacy Policy periodically to
              stay informed about how we protect your information.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">16. Compliance with Laws</h2>

            <p>We comply with applicable data protection laws, including:</p>

            <ul>
              <li>Information Technology Act, 2000 (India)</li>
              <li>
                Information Technology (Reasonable Security Practices and
                Procedures and Sensitive Personal Data or Information) Rules,
                2011
              </li>
              <li>
                General Data Protection Regulation (GDPR) for European users
              </li>
              <li>
                California Consumer Privacy Act (CCPA) for California users
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">17. Your Consent</h2>

            <p>By using QuVikme, you consent to:</p>
            <ul>
              <li>
                The collection and use of information as described in this
                Privacy Policy
              </li>
              <li>The processing of your data by our service providers</li>
              <li>
                The transfer of your information to other countries for
                processing
              </li>
              <li>Receiving service-related and promotional communications</li>
            </ul>

            <p>
              You can withdraw your consent at any time by deleting your account
              or contacting us.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">18. Contact Us</h2>

            <Alert variant="success">
              <p className="mb-2">
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us:
              </p>

              <p className="mb-2">
                <strong>QuVikme</strong>
                <br />
                Email: <a href="mailto:info@quvikme.com">info@quvikme.com</a>
                <br />
                Support Email:{" "}
                <a href="mailto:support@quvikme.org">support@quvikme.org</a>
                <br />
                Address: [Your Complete Business Address]
                <br />
                Phone: 9765910790
              </p>

              <p className="mb-0">
                <strong>Data Protection Officer (if applicable):</strong>
                <br />
                Email:{" "}
                <a href="mailto:support@quvikme.org">support@quvikme.org</a>
              </p>

              <p className="mt-2 mb-0">
                We will respond to your inquiries within 30 days.
              </p>
            </Alert>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">19. Grievance Officer</h2>

            <p>
              In accordance with Information Technology Act, 2000 and rules made
              thereunder, the name and contact details of the Grievance Officer
              are provided below:
            </p>

            <Alert variant="success">
              <p className="mb-0">
                <strong>Grievance Officer</strong>
                <br />
                Name: [Grievance Officer Name]
                <br />
                Email:{" "}
                <a href="mailto:support@quvikme.org">support@quvikme.org</a>
                <br />
                Address: [Your Complete Business Address]
                <br />
                Time: Mon - Fri (9:00 AM - 6:00 PM IST)
              </p>
            </Alert>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">
              20. Additional Information for European Users (GDPR)
            </h2>

            <p>
              If you are located in the European Economic Area (EEA), you have
              additional rights under GDPR:
            </p>

            <ul>
              <li>
                <strong>Legal Basis for Processing:</strong> We process your
                data based on consent, contract performance, legal obligations,
                and legitimate interests
              </li>
              <li>
                <strong>Right to Object:</strong> You can object to processing
                based on legitimate interests
              </li>
              <li>
                <strong>Right to Restriction:</strong> You can request
                restriction of processing in certain circumstances
              </li>
              <li>
                <strong>Right to Lodge a Complaint:</strong> You can file a
                complaint with your local data protection authority
              </li>
              <li>
                <strong>Data Protection Officer:</strong> You can contact our
                DPO at support@quvikme.org
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">
              21. Additional Information for California Users (CCPA)
            </h2>

            <p>
              If you are a California resident, you have specific rights under
              CCPA:
            </p>

            <ul>
              <li>
                <strong>Right to Know:</strong> Request information about data
                we collect, use, and share
              </li>
              <li>
                <strong>Right to Delete:</strong> Request deletion of your
                personal information
              </li>
              <li>
                <strong>Right to Opt-Out:</strong> Opt-out of the sale of
                personal information (we do not sell personal information)
              </li>
              <li>
                <strong>Right to Non-Discrimination:</strong> We will not
                discriminate against you for exercising your rights
              </li>
            </ul>

            <p>To exercise these rights, contact us at support@quvikme.org</p>
          </section>

          <div className="text-center mt-5 pt-4 border-top text-muted">
            <p className="mb-1">© 2025 QuiVikme. All rights reserved.</p>
            <p className="small">
              This Privacy Policy is effective as of December 21, 2025
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PrivacyPolicy;
